/**
 * Regras puras da troca de senha do primeiro acesso.
 *
 * As obrigatórias espelham o backend (ChangePasswordSerializer +
 * core.validators.SenhaForteValidator): mínimo de 8, diferente da senha
 * atual, letras E números e confirmação igual. O design
 * (pharu-primeiro-acesso-handoff) tratava "um número" como só recomendado;
 * aqui é obrigatório porque o servidor recusa senha sem dígito — mostrar como
 * opcional deixaria a pessoa cumprir o checklist e levar erro no envio.
 *
 * A força é só orientação: o envio exige apenas as regras obrigatórias.
 */
import { CAMPO_GERAL, errosPorCampo } from "../../../utils/errosApi";

export const TAMANHO_MINIMO_SENHA = 8;
// A partir daqui o comprimento vale um ponto extra de força.
const TAMANHO_SENHA_LONGA = 12;

export const ROTULOS_DE_FORCA = ["", "Fraca", "Razoável", "Boa", "Forte"];

// Mesmas classes de caractere do SenhaForteValidator (letra = A-Z/a-z).
const temLetra = (senha) => /[A-Za-z]/.test(senha);
const temNumero = (senha) => /\d/.test(senha);
const temMaiusculaEMinuscula = (senha) => /[a-z]/.test(senha) && /[A-Z]/.test(senha);
const temSimbolo = (senha) => /[^A-Za-z0-9]/.test(senha);

/** 0 = vazia · 1 Fraca · 2 Razoável · 3 Boa · 4 Forte (fórmula do design). */
export function forcaDaSenha(senha) {
  const s = String(senha || "");
  if (!s) return 0;
  if (s.length < TAMANHO_MINIMO_SENHA) return 1;
  const pontos =
    1 +
    (s.length >= TAMANHO_SENHA_LONGA ? 1 : 0) +
    (temMaiusculaEMinuscula(s) ? 1 : 0) +
    (temNumero(s) ? 1 : 0) +
    (temSimbolo(s) ? 1 : 0);
  if (pontos <= 2) return 2;
  return pontos === 3 ? 3 : 4;
}

/**
 * Itens do checklist, na ordem da tela. Sem `provisoria` (convite por
 * e-mail: não existe senha anterior), o "Diferente da provisória" sai.
 */
export function requisitosDaSenha(nova, provisoria) {
  const n = String(nova || "");
  const itens = [
    { id: "tamanho", texto: "Mínimo de 8 caracteres", obrigatorio: true, cumprido: n.length >= TAMANHO_MINIMO_SENHA },
    { id: "diferente", texto: "Diferente da provisória", obrigatorio: true, cumprido: n.length > 0 && n !== provisoria },
    { id: "letras_numeros", texto: "Letras e números", obrigatorio: true, cumprido: temLetra(n) && temNumero(n) },
    { id: "caixa", texto: "Maiúscula e minúscula", obrigatorio: false, cumprido: temMaiusculaEMinuscula(n) },
    { id: "simbolo", texto: "Um símbolo (! @ # $ …)", obrigatorio: false, cumprido: temSimbolo(n) },
  ];
  return provisoria === undefined ? itens.filter((item) => item.id !== "diferente") : itens;
}

export const CAMPO_PROVISORIA = "provisoria";
export const CAMPO_NOVA = "nova";
export const CAMPO_CONFIRMACAO = "confirmacao";
export const CAMPO_DO_FORMULARIO_GERAL = "geral";

export const MENSAGEM_SENHAS_NAO_CONFEREM = "As senhas não conferem.";

/**
 * Primeiro erro de preenchimento, na ordem em que a tela os mostra (um por
 * vez), ou null quando dá para enviar. Devolve `{ campo, mensagem }`.
 */
export function erroDaTroca({ provisoria, nova, confirmacao }) {
  if (!provisoria) {
    return { campo: CAMPO_PROVISORIA, mensagem: "Informe a senha provisória que você recebeu." };
  }
  if (nova.length >= TAMANHO_MINIMO_SENHA && nova === provisoria) {
    return { campo: CAMPO_NOVA, mensagem: "A nova senha deve ser diferente da provisória." };
  }
  return erroDaSenhaNova({ nova, confirmacao });
}

/** Mesmas regras, sem senha anterior: a senha criada pelo convite. */
export function erroDaSenhaNova({ nova, confirmacao }) {
  if (nova.length < TAMANHO_MINIMO_SENHA) {
    return { campo: CAMPO_NOVA, mensagem: "A nova senha precisa ter pelo menos 8 caracteres." };
  }
  if (!temLetra(nova) || !temNumero(nova)) {
    return { campo: CAMPO_NOVA, mensagem: "A nova senha precisa ter letras e números." };
  }
  if (confirmacao !== nova) {
    return { campo: CAMPO_CONFIRMACAO, mensagem: MENSAGEM_SENHAS_NAO_CONFEREM };
  }
  return null;
}

export const CONFERE = "confere";
export const NAO_CONFERE = "nao_confere";

/**
 * Aviso ao vivo abaixo da confirmação. "Não confere" só depois que a
 * confirmação alcançou o tamanho da nova — antes disso a pessoa ainda está
 * digitando.
 */
export function conferenciaDaConfirmacao(nova, confirmacao) {
  if (!confirmacao) return "";
  if (confirmacao === nova) return nova.length >= TAMANHO_MINIMO_SENHA ? CONFERE : "";
  return confirmacao.length >= nova.length ? NAO_CONFERE : "";
}

// Nome do campo no ChangePasswordSerializer → campo da tela.
const CAMPO_DO_SERVIDOR = {
  senha_atual: CAMPO_PROVISORIA,
  nova_senha: CAMPO_NOVA,
  confirmar_nova_senha: CAMPO_CONFIRMACAO,
};

/**
 * Recusa do backend → `{ campo: mensagem }` da tela. Senha provisória
 * errada vai para o campo dela; palavra bloqueada ou senha comum, para a nova;
 * limite de tentativas, rede e o resto, para a mensagem acima do botão.
 */
export function falhaDaTroca(erro) {
  const doServidor = errosPorCampo(erro);
  const naTela = {};
  Object.entries(doServidor).forEach(([campo, mensagem]) => {
    const destino = campo === CAMPO_GERAL ? CAMPO_DO_FORMULARIO_GERAL : CAMPO_DO_SERVIDOR[campo];
    naTela[destino || CAMPO_DO_FORMULARIO_GERAL] = mensagem;
  });
  if (Object.keys(naTela).length === 0) {
    naTela[CAMPO_DO_FORMULARIO_GERAL] = "Não foi possível definir a senha. Tente novamente.";
  }
  return naTela;
}
